import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  BadgeSizes,
  ButtonVariants,
  Table,
  Typography,
  TypographyColors,
  TypographyElements,
  TypographyVariants,
  TypographyWeights,
} from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { CERTIFICATE_TYPE_TONE } from '@/config/statusTones';
import { buildTableLabels } from '@/config/tableLabels';
import { ConfirmDialog } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { useDeleteTerminalMutation } from '../../api/sitesApi';
import { MAX_TERMINALS } from '../../constants/sites.constants';
import { buildSiteTerminalsColumns } from '../../constants/siteTerminalsColumns';
import { filterTerminals } from '../../utils/sitesUtils';
import { TerminalModal } from './TerminalModal';
import styles from './SiteOverviewTabView.module.scss';

const getTerminalId = (row) => row.id;

/**
 * Props:
 *   site  the site detail view model
 */
export const SiteOverviewTabView = ({ site }) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [terminalToEdit, setTerminalToEdit] = useState(null);
  const [terminalToDelete, setTerminalToDelete] = useState(null);
  const [deleteTerminal] = useDeleteTerminalMutation();

  const freeSlots = MAX_TERMINALS - site.terminals.length;

  const handleSearchChange = useCallback((value) => setSearch(value), []);

  const handleAddTerminals = useCallback(() => setIsAddOpen(true), []);
  const handleCloseAdd = useCallback(() => setIsAddOpen(false), []);
  const handleEditTerminal = useCallback((row) => setTerminalToEdit(row), []);
  const handleCloseEdit = useCallback(() => setTerminalToEdit(null), []);

  const handleDeleteTerminal = useCallback((row) => setTerminalToDelete(row), []);
  const handleCancelDelete = useCallback(() => setTerminalToDelete(null), []);
  const handleConfirmDelete = useCallback(async () => {
    await deleteTerminal({ id: terminalToDelete.id, siteId: site.id });
    setTerminalToDelete(null);
  }, [deleteTerminal, terminalToDelete, site.id]);

  const columns = useMemo(
    () =>
      buildSiteTerminalsColumns(t, {
        onEdit: handleEditTerminal,
        onDelete: handleDeleteTerminal,
      }),
    [t, handleEditTerminal, handleDeleteTerminal],
  );
  const rows = useMemo(() => filterTerminals(site.terminals, search), [site.terminals, search]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const tiles = useMemo(
    () => [
      {
        id: 'certificateType',
        label: t('FIELD_CERTIFICATION_TYPE'),
        value: site.certificateTypeLabel,
        tone: CERTIFICATE_TYPE_TONE[site.certificateType],
      },
      {
        id: 'certificationStatus',
        label: t('FIELD_CERTIFICATION_STATUS'),
        value: site.certificationStatus,
      },
      {
        id: 'isccCertificateNumber',
        label: t('FIELD_ISCC_CERTIFICATE_NUMBER'),
        value: site.isccCertificateNumber,
      },
      { id: 'validFrom', label: t('FIELD_VALID_FROM'), value: site.validFrom },
      { id: 'validTo', label: t('FIELD_VALID_TILL'), value: site.validTo },
    ],
    [t, site],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'add-terminals',
        label: t('SITES_ADD_TERMINALS'),
        variant: ButtonVariants.primary,
        disabled: freeSlots <= 0,
        onClick: handleAddTerminals,
      },
    ],
    [t, freeSlots, handleAddTerminals],
  );

  const emptyState = useMemo(
    () => ({
      title: t('SITES_TERMINALS_EMPTY_TITLE'),
      description: t('SITES_TERMINALS_EMPTY_DESCRIPTION'),
      primaryLabel: t('SITES_ADD_TERMINAL'),
      onPrimaryClick: handleAddTerminals,
      primaryIcon: <Plus />,
    }),
    [t, handleAddTerminals],
  );

  const noResults = useMemo(
    () => ({
      title: t('SITES_TERMINALS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <div className={styles.card}>
      <div className={styles.kpiRow}>
        {tiles.map(({ id, label, value, tone }) => (
          <div key={id} className={styles.tile}>
            <Typography
              as={TypographyElements.span}
              variant={TypographyVariants.bodyXs}
              weight={TypographyWeights.semibold}
              color={TypographyColors.secondary}
            >
              {label}
            </Typography>
            {tone ? (
              <Badge variant={tone} size={BadgeSizes.sm} className={styles.tileBadge}>
                {value}
              </Badge>
            ) : (
              <Typography
                as={TypographyElements.span}
                variant={TypographyVariants.bodySm}
                color={TypographyColors.primary}
                className={styles.tileValue}
              >
                {value}
              </Typography>
            )}
          </div>
        ))}
      </div>

      <Table
        columns={columns}
        data={rows}
        getRowId={getTerminalId}
        searchable
        onSearchChange={handleSearchChange}
        toolbarActions={toolbarActions}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <TerminalModal
        open={isAddOpen}
        onClose={handleCloseAdd}
        siteId={site.id}
        siteName={site.siteName}
        freeSlots={freeSlots}
      />

      <TerminalModal
        open={terminalToEdit !== null}
        onClose={handleCloseEdit}
        siteId={site.id}
        siteName={site.siteName}
        terminal={terminalToEdit}
      />

      <ConfirmDialog
        open={terminalToDelete !== null}
        title={t('SITES_TERMINAL_DELETE_TITLE')}
        bodyTitle={t('SITES_TERMINAL_DELETE_BODY_TITLE', {
          terminalName: terminalToDelete?.terminalName,
        })}
        description={t('SITES_TERMINAL_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
